import { Dayjs } from "dayjs";

const API_DATETIME_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

/**
 * Format process-history / applicant API datetimes without timezone conversion.
 * Backend sends Asia/Colombo wall time (often `YYYY-MM-DDTHH:mm:ss` with no offset).
 * Using `Date` + `toLocaleString` can shift values when `Z` or UTC parsing applies.
 */
export function formatApiLocalDateTime(dateString?: string | null): string {
  if (!dateString) return '';
  const trimmed = dateString.trim();
  if (!trimmed) return '';

  const withoutOffset = trimmed.replace(/(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/i, '');
  const match = withoutOffset.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!match) return trimmed;

  const year = match[1];
  const monthIndex = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const hour24 = parseInt(match[4], 10);
  const minute = match[5];

  if (monthIndex < 0 || monthIndex > 11 || Number.isNaN(day) || Number.isNaN(hour24)) {
    return trimmed;
  }

  const period = hour24 >= 12 ? 'PM' : 'AM';
  const hour12 = hour24 % 12 || 12;
  const hourStr = hour12.toString().padStart(2, '0');

  return `${API_DATETIME_MONTHS[monthIndex]} ${day}, ${year}, ${hourStr}:${minute} ${period}`;
}

export const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  // Guard against Go/MySQL zero-date sentinel (0001-01-01) that leaks through
  // when no date was provided in a CV and parseTime=true is set on the DB DSN.
  if (date.getFullYear() < 100) return '';
  return date.toISOString().split('T')[0].replace(/-/g, '.');
};

export const formatDateForApi = (date: Dayjs | null): string => {
  return date ? date.format('YYYY-MM-DD') : '';
};